const UNSUBSCRIBE_LINK_TOKEN = '{unsubscribeLink}';
const VIEW_IN_BROWSER_LINK_TOKEN = '{viewInBrowserLink}';

/*
********************************************************************************
The REGEX used below should match the regex used in 
`generateAnchorRegexForHrefValue` in EmailTemplate.php!! If either regex is
updated they should both be changed!

The only different here is because we're parsing a JSON string instead of an
HTML string, we need to escape the quotes around the href in the `a` tag
********************************************************************************
*/

/**
 * This function creates a regular expression, which can be used to find and
 * replace a link inside of an `a` tag element in a string. The regex returned
 * can be used to split the string into three elements, the text the searched
 * string, the searched string, and the text after the searched string.
 *
 * @param {string} linkTarget The string that our regex will look for inside a link
 *
 * @returns RegExp A regular expression that can be used to find the provided
 * string in a link element
 */
function generateLinkRegex(linkTarget: string): RegExp {
    return new RegExp(
        `(<a\\s+(?:[^>]*?\\s+)?href=\\\\\\")(?<href>${linkTarget})(\\\\\\"\\s*(?:[^>]*?\\s*)?>)`,
        'g'
    );
}

function replaceLinkTargetInJsonString(template: string, target: string, newLink: string): string {
    const linkRegex = generateLinkRegex(target);

    const templateWithNewUrl = template.replace(
        linkRegex,
        // fullMatch is the full text matched; `<a href="google.com">Google!</a>`
        // preTarget is the text before the link; `<a href="`
        // targetMatch is the link; `google.com`
        // postTarget is the text after the link; `">Google!</a>`
        (fullMatch, preTarget, targetMatch, postTarget) => preTarget + newLink + postTarget
    );

    return templateWithNewUrl;
}

/**
 * This function takes a JSON string of an email marketing template, replaces
 * the unsubscribe link token with a URL to an example unsubscribe page for this
 * edition, and returns it.
 *
 * @param {string} template The JSON string of the template
 * @param {string} editionId The id of the edition; needed for the URL
 * @returns The string with the unsubscribe token replaced with the link
 */
export function beeJsonStringReplaceUnsubscribeToken(template: string, editionId: string): string {
    const unsubscribeLink = `${location.origin}/unsubscribe/${editionId}`;

    return replaceLinkTargetInJsonString(template, UNSUBSCRIBE_LINK_TOKEN, unsubscribeLink);
}

/**
 * This function takes a JSON string of an email marketing template with a link
 * to an example unsubscribe page, and replaces that link with the unsubscribe
 * link token.
 *
 * @param {string} template The JSON string of the email template
 * @returns The string with the unsubscribe link replaced with the token
 */
export function beeJsonStringReplaceUnsubscribeUrl(template: string): string {
    const unsubscribeLinkRegex = `${location.origin}/unsubscribe/\\d+-emEditions`;

    return replaceLinkTargetInJsonString(template, unsubscribeLinkRegex, UNSUBSCRIBE_LINK_TOKEN);
}

/**
 * This function takes a JSON string of an email marketing template, replaces
 * the view in browser link token with a URL to the edition's view in browser
 * page, and returns it.
 *
 * @param {string} template The JSON string of the template
 * @param {string} editionPreviewUrl The url of the view page
 * @returns The string with the view in broswer token replaced with the link
 */
export function beeJsonStringReplaceDetailsToken(
    template: string,
    editionPreviewUrl: string
): string {
    return replaceLinkTargetInJsonString(template, VIEW_IN_BROWSER_LINK_TOKEN, editionPreviewUrl);
}

/**
 * This function takes a JSON string of an email marketing template with a link
 * to an edition's view in broswer page, and replaces that link with the view in
 * browser link token.
 *
 * @param {string} template The JSON string of the email template
 * @param {string} editionPreviewUrl The url of the view page
 * @returns The string with the view in browser link replaced with the token
 */
export function beeJsonStringReplaceDetailsUrl(
    template: string,
    editionPreviewUrl: string
): string {
    return replaceLinkTargetInJsonString(template, editionPreviewUrl, VIEW_IN_BROWSER_LINK_TOKEN);
}
